/**
 * Topbar live-search combobox.
 *
 * Renders the sky-blue header's "Search people..." input plus a floating
 * results dropdown that opens as the user types (≥2 chars, ~250ms debounce).
 * Reuses the shared `useUserList` hook from `@/lib/api` for data — the People
 * page uses an infinite version of the same endpoint.
 *
 * UX:
 *   - Up to 8 results, each a clickable row linking to `/user/{username}`
 *     (the canonical profile path used by `UserCard` and the Media tab).
 *   - "See all results" footer routes to `/users?q=<query>` (the People page).
 *   - States: empty (<2 chars) → no dropdown · loading · empty · error.
 *   - Closes on outside click, Esc, or selecting a result.
 *   - Keyboard: ArrowDown / ArrowUp / Enter; ARIA combobox + listbox roles.
 *
 * Submit (Enter on the input with no highlighted row) navigates to the full
 * People search page — preserving the legacy `/users/search/q/:query` route
 * the old Topbar already used so existing deep-links keep working.
 */
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, Search } from 'lucide-react';

import { Input, UserAvatar } from '@/components/ui';
import { useUserList } from '@/lib/api';
import { cn } from '@/lib/utils';
import { fullName, initials } from '@/pages/profile/lib/profile';
import type { User } from '@/types/api';

const MIN_CHARS = 2;
const DEBOUNCE_MS = 250;
const MAX_RESULTS = 8;

/** Build the canonical profile link. Falls back to `/users` when no username. */
function profileHref(user: User): string {
  return user.username ? `/user/${user.username}` : '/users';
}

interface TopbarSearchProps {
  /**
   * Force-visible on mobile. The desktop topbar always renders the search
   * inline (`hidden sm:block`); below 640 the parent `Topbar` hides it behind
   * a search-icon button and re-mounts this component with `mobileOpen=true`
   * to swap in a full-width input that auto-focuses for instant typing.
   */
  mobileOpen?: boolean;
}

export function TopbarSearch({ mobileOpen = false }: TopbarSearchProps = {}) {
  const navigate = useNavigate();
  const listboxId = useId();

  const [input, setInput] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1); // -1 = nothing highlighted

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  /* ---------------------------- Debounce input ---------------------------- */
  useEffect(() => {
    const trimmed = input.trim();
    // Always defer the state update so we never set state synchronously
    // inside the effect — React Compiler flags that as cascading renders.
    const t = setTimeout(() => {
      setDebounced(trimmed.length < MIN_CHARS ? '' : trimmed);
    }, trimmed.length < MIN_CHARS ? 0 : DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [input]);

  /* ------------------------------- Fetch ---------------------------------- */
  const enabled = debounced.length >= MIN_CHARS;
  const { data, isLoading, isFetching, isError } = useUserList(
    { q: debounced, type: 'latest', limit: MAX_RESULTS },
    { enabled },
  );

  const results = useMemo<User[]>(
    () => (enabled ? (data?.list ?? []).slice(0, MAX_RESULTS) : []),
    [enabled, data],
  );

  // Clamp the highlight to the current result set instead of resetting via
  // a setState-in-effect (which React Compiler flags as cascading renders).
  // -1 means "nothing highlighted"; values past the end fall back to -1.
  const activeIndex = active >= 0 && active < results.length ? active : -1;

  /* --------------------------- Outside click ------------------------------ */
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  /* --------------------------- Keyboard nav ------------------------------- */
  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      if (open) {
        e.preventDefault();
        setOpen(false);
        setActive(-1);
      }
      return;
    }
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((prev) => (prev + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((prev) =>
        prev <= 0 ? results.length - 1 : prev - 1,
      );
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      const picked = results[activeIndex];
      if (picked) {
        navigate(profileHref(picked));
        closeAndReset();
      }
    }
  }

  /* ------------------------------ Submit ---------------------------------- */
  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const value = input.trim();
    if (!value) return;
    // Honour an active highlight first (covers Enter without a key handler).
    if (open && activeIndex >= 0 && results[activeIndex]) {
      navigate(profileHref(results[activeIndex]!));
      closeAndReset();
      return;
    }
    navigate(`/users/search/q/${encodeURIComponent(value)}`);
    setOpen(false);
  }

  const closeAndReset = useCallback(() => {
    setOpen(false);
    setActive(-1);
  }, []);

  function handleFocus() {
    if (input.trim().length >= MIN_CHARS) setOpen(true);
  }

  function handleChange(value: string) {
    setInput(value);
    setOpen(value.trim().length >= MIN_CHARS);
  }

  /* --------------------------------- UI ----------------------------------- */
  const showDropdown =
    open && input.trim().length >= MIN_CHARS;
  const showLoading = showDropdown && enabled && (isLoading || (isFetching && results.length === 0));
  const showEmpty =
    showDropdown && enabled && !isLoading && !isError && results.length === 0;
  const showError = showDropdown && enabled && isError;

  const seeAllHref = `/users?q=${encodeURIComponent(input.trim())}`;
  const activeId =
    activeIndex >= 0 ? `${listboxId}-opt-${activeIndex}` : undefined;

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative mx-auto w-full max-w-md',
        // When `mobileOpen`, parent has replaced its content with this search
        // overlay — render visible at every viewport. Otherwise the default
        // is desktop-only (the mobile entry point is a search-icon button).
        mobileOpen ? 'block' : 'hidden sm:block',
      )}
    >
      <form onSubmit={handleSubmit} role="search">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/80" />
          <Input
            ref={inputRef}
            type="text"
            role="combobox"
            value={input}
            onChange={(e) => handleChange(e.target.value)}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            placeholder="Search people…"
            aria-label="Search people"
            aria-expanded={showDropdown}
            aria-haspopup="listbox"
            aria-autocomplete="list"
            aria-controls={listboxId}
            aria-activedescendant={activeId}
            autoComplete="off"
            autoFocus={mobileOpen}
            className="rounded-full border-transparent bg-white/20 pl-9 text-white placeholder:text-white/80 focus-visible:bg-white/25 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
          />
        </div>
      </form>

      {showDropdown ? (
        <div
          // z-30: above page content, below the sticky topbar (z-40) and
          // the Toast viewport so floating system UI still wins.
          className="absolute left-0 right-0 top-full z-30 mt-2 overflow-hidden rounded-md border border-border bg-surface text-ink shadow-lg"
        >
          {showLoading ? (
            <div className="flex items-center gap-2 px-4 py-3 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              <span>Searching…</span>
            </div>
          ) : showError ? (
            <div className="px-4 py-3 text-sm text-muted-foreground">
              Couldn’t search. Try again.
            </div>
          ) : showEmpty ? (
            <div className="px-4 py-3 text-sm text-muted-foreground">
              No people match “{input.trim()}”.{' '}
              <Link
                to={seeAllHref}
                className="font-medium text-brand-700 hover:underline"
                onClick={closeAndReset}
              >
                Try People search
              </Link>
              .
            </div>
          ) : (
            <>
              <ul
                id={listboxId}
                role="listbox"
                aria-label="People search results"
                className="max-h-[60vh] overflow-y-auto py-1"
              >
                {results.map((user, idx) => {
                  const name = fullName(user) || user.username || 'Member';
                  const isActive = idx === activeIndex;
                  return (
                    <li
                      key={user.ID}
                      id={`${listboxId}-opt-${idx}`}
                      role="option"
                      aria-selected={isActive}
                    >
                      <Link
                        to={profileHref(user)}
                        onClick={closeAndReset}
                        onMouseEnter={() => setActive(idx)}
                        className={cn(
                          'flex items-center gap-3 px-3 py-2 text-sm transition-colors',
                          isActive
                            ? 'bg-brand-50 text-ink'
                            : 'hover:bg-muted/60',
                        )}
                      >
                        <UserAvatar
                          src={user.image}
                          alt={name}
                          fallback={initials(user)}
                          plan={user.plan}
                          className="h-8 w-8"
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-medium leading-tight">
                            {name}
                          </span>
                          {user.username ? (
                            <span className="block truncate text-xs text-muted-foreground">
                              @{user.username}
                            </span>
                          ) : null}
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
              <Link
                to={seeAllHref}
                onClick={closeAndReset}
                className="block border-t border-border px-3 py-2 text-center text-xs font-medium text-brand-700 hover:bg-muted/60"
              >
                See all results
              </Link>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
